import { Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import * as RingoverSdkModule from 'ringover-sdk';
import { PlatformService } from './platform.service';
import { UserService } from './user.service';

interface RingoverSdkInstance {
  generate(): HTMLIFrameElement | false;
  destroy(): boolean;
  checkStatus(): boolean;
  show(): boolean;
  hide(): boolean;
  isDisplay(): boolean;
  dial(numberE164: string | number, fromNumberE164?: string | number | null): boolean;
  on(eventName: string, callback: (event: unknown) => void): boolean;
  off(): boolean;
}

type RingoverSdkConstructor = new (options?: Record<string, unknown>) => RingoverSdkInstance;

export interface RingoverCallOutcome {
  e164: string;
  call_id: string | null;
  call_duration: number;
  ring_duration: number;
  /** true if the callee answered (call_duration > 0) */
  answered: boolean;
  action_type: 'call' | 'call_no_answer';
}

interface RingoverCallEventPayload {
  action?: string;
  data?: {
    direction?: string;
    from_number?: string;
    to_number?: string;
    call_id?: string;
    ringDuration?: number;
    callDuration?: number;
  };
}

function resolveRingoverSdk(): RingoverSdkConstructor {
  const mod = RingoverSdkModule as unknown as RingoverSdkConstructor | { default: RingoverSdkConstructor };
  return typeof mod === 'function' ? mod : mod.default;
}

@Injectable({
  providedIn: 'root',
})
export class RingoverDialerService {
  private user_service = inject(UserService);
  private platform = inject(PlatformService);

  private sdk: RingoverSdkInstance | null = null;
  private generated = false;
  private dialer_ready = false;
  private escape_listener_attached = false;
  private media_armed = false;
  private remount_in_progress = false;
  private dialer_was_displayed = false;
  private visibility_watcher_timer: number | null = null;

  private pending_e164: string | null = null;
  private pending_outcome$: Subject<RingoverCallOutcome> | null = null;

  private readonly on_escape_key = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') {
      return;
    }
    if (this.sdk?.isDisplay()) {
      this.hide();
    }
  };

  isEnabledForCurrentUser(): boolean {
    return !!this.user_service.getCurrentUser()?.ringover_enabled;
  }

  /** Lazy-init dialer iframe (tray icon, bottom-right). No-op if user has Ringover disabled. */
  ensureInitialized(): void {
    if (typeof document === 'undefined') {
      return;
    }

    if (!this.isEnabledForCurrentUser()) {
      this.destroy();
      return;
    }

    if (!this.sdk) {
      const RingoverSDK = resolveRingoverSdk();
      this.sdk = new RingoverSDK({
        type: 'fixed',
        size: 'medium',
        trayicon: true,
        animation: true,
        position: { bottom: '0', right: '64px' },
        trayposition: { bottom: '10px', right: '10px' },
      });

      this.sdk.on('dialerReady', () => {
        this.dialer_ready = true;
      });
      this.sdk.on('login', () => {
        this.dialer_ready = true;
      });
      this.sdk.on('logout', () => {
        this.dialer_ready = false;
      });
      this.sdk.on('ringingCall', () => {
        // Incoming (or outbound ring): allow mic without remounting so we don't kill the call.
        void this.armMicrophone({ remount: false });
      });
      this.sdk.on('answeredCall', () => {
        void this.armMicrophone({ remount: false });
      });
      this.sdk.on('hangupCall', (event: unknown) => {
        this.handleHangupCall(event as RingoverCallEventPayload);
        void this.releaseMicrophoneAfterCall();
      });
    }

    if (!this.generated) {
      // Mic stays denied in Electron while the dialer is collapsed (Bluetooth A2DP).
      // Armed when the dialer is shown or a call starts.
      void this.platform.setRingoverMediaCaptureAllowed(false);
      this.media_armed = false;
      this.sdk.generate();
      this.generated = true;
      // Start collapsed so the tray icon is visible without blocking the UI
      this.sdk.hide();
      this.attachEscapeListener();
      this.startDialerVisibilityWatcher();
    }
  }

  hide(): void {
    this.sdk?.hide();
  }

  destroy(): void {
    this.stopDialerVisibilityWatcher();
    this.detachEscapeListener();
    this.clearPendingCall(new Error('Dialer cerrado'));
    void this.platform.setRingoverMediaCaptureAllowed(false);
    this.media_armed = false;
    this.dialer_was_displayed = false;
    if (this.sdk && this.generated) {
      try {
        this.sdk.destroy();
      } catch {
        // ignore if already removed from DOM
      }
    }
    this.sdk = null;
    this.generated = false;
    this.dialer_ready = false;
  }

  isDialerReady(): boolean {
    return this.dialer_ready;
  }

  /**
   * Normalize a phone string to E.164.
   * Default country ES (+34) when no international prefix is present.
   */
  toE164(phone: string, default_country_code = '34'): string | null {
    if (!phone) {
      return null;
    }

    let raw = phone.trim().replace(/[\s\-().]/g, '');
    if (!raw) {
      return null;
    }

    if (raw.startsWith('00')) {
      raw = '+' + raw.slice(2);
    }

    if (raw.startsWith('+')) {
      const digits = raw.slice(1).replace(/\D/g, '');
      return digits.length >= 8 ? `+${digits}` : null;
    }

    let digits = raw.replace(/\D/g, '');
    if (!digits) {
      return null;
    }

    // Already includes country code without +
    if (digits.startsWith(default_country_code) && digits.length >= 8 + default_country_code.length) {
      return `+${digits}`;
    }

    // Spanish national numbers often start with 0 (e.g. 06xx → 6xx)
    if (default_country_code === '34' && digits.startsWith('0') && digits.length === 10) {
      digits = digits.slice(1);
    }

    return `+${default_country_code}${digits}`;
  }

  /**
   * Dial and emit once when Ringover reports hangup for that outbound call.
   * action_type is `call` if call_duration > 0, otherwise `call_no_answer`.
   */
  callWithOutcome(phone: string): Observable<RingoverCallOutcome> {
    if (!this.isEnabledForCurrentUser()) {
      throw new Error('No tienes la integración Ringover/Quicktalk habilitada');
    }

    this.ensureInitialized();

    const e164 = this.toE164(phone);
    if (!e164) {
      throw new Error('Número de teléfono no válido');
    }

    if (!this.sdk) {
      throw new Error('No se pudo inicializar el dialer Quicktalk/Ringover');
    }

    if (this.pending_outcome$) {
      throw new Error('Ya hay una llamada en curso desde la intranet. Espera a que termine.');
    }

    this.pending_e164 = e164;
    this.pending_outcome$ = new Subject<RingoverCallOutcome>();

    void this.startOutboundCall(e164);

    return this.pending_outcome$.asObservable().pipe(take(1));
  }

  private async startOutboundCall(e164: string): Promise<void> {
    try {
      // Remount so Ringover can acquire the mic after Electron grants permission.
      await this.armMicrophone({ remount: true });
      await this.waitUntilDialerReady(15000);

      if (!this.sdk) {
        throw new Error('No se pudo inicializar el dialer Quicktalk/Ringover');
      }

      this.sdk.show();

      const ok = this.sdk.dial(e164);
      if (!ok) {
        throw new Error('No se pudo iniciar la llamada. Revisa el dialer Quicktalk/Ringover');
      }
    } catch (err) {
      this.clearPendingCall(err instanceof Error ? err : new Error(String(err)));
      void this.releaseMicrophoneAfterCall();
    }
  }

  private async armMicrophone(options: { remount: boolean }): Promise<void> {
    // macOS OS-level prompt (Electron only; no-op / granted elsewhere).
    await this.platform.requestMicrophoneAccess();
    await this.platform.setRingoverMediaCaptureAllowed(true);
    this.media_armed = true;

    // Remount only in Electron: permission was denied while idle, so Ringover needs a fresh
    // browsing context to re-request getUserMedia after we grant it.
    if (!this.platform.isElectronApp() || !options.remount || !this.sdk || !this.generated || this.remount_in_progress) {
      return;
    }

    const keep_visible = this.sdk.isDisplay();
    this.remountDialer({ show_after: keep_visible });
  }

  private async releaseMicrophoneAfterCall(): Promise<void> {
    if (!this.platform.isElectronApp()) {
      this.media_armed = false;
      return;
    }

    // Keep mic armed while the dialer UI is still open (user may click LISTO / call again).
    if (this.sdk?.isDisplay()) {
      return;
    }

    await this.platform.setRingoverMediaCaptureAllowed(false);
    this.media_armed = false;

    // Tear down the iframe browsing context so getUserMedia tracks are released
    // (otherwise macOS keeps headphones in call/HFP mode).
    if (this.sdk && this.generated && !this.remount_in_progress) {
      this.remountDialer({ show_after: false });
    }
  }

  private remountDialer(options: { show_after: boolean } = { show_after: false }): void {
    if (!this.sdk || !this.generated || this.remount_in_progress) {
      return;
    }

    this.remount_in_progress = true;
    this.dialer_ready = false;

    try {
      this.sdk.destroy();
    } catch {
      // ignore
    }

    this.generated = false;

    try {
      this.sdk.generate();
      this.generated = true;
      if (options.show_after) {
        this.sdk.show();
        this.dialer_was_displayed = true;
      } else {
        this.sdk.hide();
        this.dialer_was_displayed = false;
      }
    } catch {
      this.generated = false;
    } finally {
      this.remount_in_progress = false;
    }
  }

  /**
   * Tray / manual dialer open does not go through callWithOutcome. Arm mic as soon as
   * the iframe is visible so LISTO can acquire getUserMedia; release when collapsed.
   */
  private startDialerVisibilityWatcher(): void {
    if (!this.platform.isElectronApp() || this.visibility_watcher_timer !== null || typeof window === 'undefined') {
      return;
    }

    this.visibility_watcher_timer = window.setInterval(() => {
      if (!this.sdk || !this.generated || this.remount_in_progress) {
        return;
      }

      const displayed = this.sdk.isDisplay();

      if (displayed && !this.dialer_was_displayed) {
        this.dialer_was_displayed = true;
        // No remount: would close/reopen the dialer the user just opened.
        void this.armMicrophone({ remount: false });
        return;
      }

      if (!displayed && this.dialer_was_displayed) {
        this.dialer_was_displayed = false;
        if (!this.pending_outcome$) {
          void this.releaseMicrophoneAfterCall();
        }
      }
    }, 400);
  }

  private stopDialerVisibilityWatcher(): void {
    if (this.visibility_watcher_timer === null || typeof window === 'undefined') {
      return;
    }

    window.clearInterval(this.visibility_watcher_timer);
    this.visibility_watcher_timer = null;
  }

  private waitUntilDialerReady(timeout_ms: number): Promise<void> {
    if (this.dialer_ready) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const started = Date.now();
      const timer = window.setInterval(() => {
        if (this.dialer_ready) {
          window.clearInterval(timer);
          resolve();
          return;
        }
        if (Date.now() - started >= timeout_ms) {
          window.clearInterval(timer);
          reject(new Error('Inicia sesión en el dialer Quicktalk/Ringover para poder llamar'));
        }
      }, 200);
    });
  }

  private handleHangupCall(event: RingoverCallEventPayload): void {
    if (!this.pending_outcome$ || !this.pending_e164) {
      return;
    }

    const data = event?.data;
    if (!data || data.direction !== 'out') {
      return;
    }

    const remote_number = data.to_number ?? '';
    if (!this.phonesMatch(remote_number, this.pending_e164)) {
      return;
    }

    const call_duration = Number(data.callDuration) || 0;
    const ring_duration = Number(data.ringDuration) || 0;
    const answered = call_duration > 0;
    const outcome: RingoverCallOutcome = {
      e164: this.pending_e164,
      call_id: data.call_id != null ? String(data.call_id) : null,
      call_duration,
      ring_duration,
      answered,
      action_type: answered ? 'call' : 'call_no_answer',
    };

    const subject = this.pending_outcome$;
    this.pending_outcome$ = null;
    this.pending_e164 = null;
    subject.next(outcome);
    subject.complete();
  }

  private phonesMatch(a: string, b: string): boolean {
    const da = a.replace(/\D/g, '');
    const db = b.replace(/\D/g, '');
    if (!da || !db) {
      return false;
    }
    // Compare last 9 digits (ES national) or full digit strings
    if (da === db) {
      return true;
    }
    const tail = (s: string) => (s.length > 9 ? s.slice(-9) : s);
    return tail(da) === tail(db);
  }

  private clearPendingCall(error: Error): void {
    if (this.pending_outcome$) {
      this.pending_outcome$.error(error);
      this.pending_outcome$ = null;
    }
    this.pending_e164 = null;
  }

  private attachEscapeListener(): void {
    if (this.escape_listener_attached || typeof document === 'undefined') {
      return;
    }
    document.addEventListener('keydown', this.on_escape_key);
    this.escape_listener_attached = true;
  }

  private detachEscapeListener(): void {
    if (!this.escape_listener_attached || typeof document === 'undefined') {
      return;
    }
    document.removeEventListener('keydown', this.on_escape_key);
    this.escape_listener_attached = false;
  }
}
